import React, { memo, useEffect } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import moment from 'moment';
import { addPageNumber } from '../common/addPageNumber';

interface SiteAssignmentPdfReportPDFProps {
    data: any;
    setPdfBlob: (blob: Blob) => void;
}

const SiteAssignmentPdf: React.FC<SiteAssignmentPdfReportPDFProps> = ({ data, setPdfBlob }) => {
    const todayDate = moment().format('DD-MM-YYYY');

    const generatePDF = () => {
        const doc = new jsPDF({
            orientation: 'landscape',
            unit: 'mm',
            format: 'a4'
        });

        let yOffset = 20;
        // Title
        doc.setFontSize(20);
        doc.text('Sites Assignments', 10,12);

        const sortedData = [...data].sort((a, b) => a.DIST_NAM.localeCompare(b.DIST_NAM));

        const addDistrictSiteAndOthers = (district: string, site: string) => {
            doc.setFontSize(13);
            doc.setFont('helvetica', 'bold');

            const leftX = 15; // Left margin
            const centerX = doc.internal.pageSize.getWidth() / 2; // Center position
            const rightX = doc.internal.pageSize.getWidth() - 14; // Right margin

            // Set header text to bold
            doc.text(`District:`, leftX, yOffset);
            doc.setFont('helvetica', 'normal'); // Change to normal for the value
            doc.text(`${district ?? ''}`, leftX + 19, yOffset); // Adjust position for the value

            doc.setFont('helvetica', 'bold');
            doc.text(`Site:`, centerX, yOffset, { align: 'center' });
            doc.setFont('helvetica', 'normal');
            doc.text(`${site ?? ''}`, centerX + 8, yOffset); // Adjust position for the value

            doc.setFont('helvetica', 'bold');
            doc.text('Others', rightX, yOffset, { align: 'right' });
            yOffset += 5;
        };

        sortedData?.forEach((data: any, index: number) => {
            if (index > 0) {
                // Add new page for each new data row after the first one
                doc.addPage();
                yOffset = 20; // Reset yOffset for new page
            }

        addDistrictSiteAndOthers(data?.DIST_NAM, data?.SITE_NAM);
        
        // Table Headers
        // 'Phones' spans the phone/cell column and the Ext column, which are split so that
        // Ext always starts at the same x whether or not a phone number is present.
        const tableHeaders = [
            [
                '',
                'Name',
                { content: 'Phones', colSpan: 2 },
                'Forms',
                'E-mail',
            ]
        ];

        // Table Body
        const tableBody = [
            [
                { content: 'District Field Manager', styles: { fontStyle: 'bold' } },
                data?.FieldSupervisorName,
                `Phone  ${data?.FieldSupervisorFax ?? ''} \nCell ${data?.FieldSupervisorPhone ?? ''}`,
                `Ext ${data?.FieldSupervisorExt ?? ''}`,
                data?.FieldSupervisorForms,
                data?.FieldSupervisorEmail,
            ],
            [
                { content: 'In-Service/ID Labels', styles: { fontStyle: 'bold' } },
                data?.FieldTrainerName,
                `Phone  ${data?.FieldTrainerFax ?? ''} \nCell ${data?.FieldTrainerPhone ?? ''}`,
                `Ext ${data?.FieldTrainerExt ?? ''}`,
                data?.FieldTrainerForms,
                data?.FieldTrainerEmail,
            ],
            [
                { content: 'Registrar', styles: { fontStyle: 'bold' } },
                data?.RegistarName,
                `Phone  ${data?.RegistarFax ?? ''} \nCell ${data?.RegistarPhone ?? ''}`,
                `Ext ${data?.RegistarExt ?? ''}`,
                data?.RegistarForms,
                data?.RegistarEmail,
            ],
            [
                { content: 'Account Billing Clerk', styles: { fontStyle: 'bold' } },
                data?.AccountBillingName,
                `Phone  ${data?.AccountBillingFax ?? ''} \nCell ${data?.AccountBillingPhone ?? ''}`,
                `Ext ${data?.AccountBillingExt ?? ''}`,
                data?.AccountBillingForms,
                data?.AccountBillingEmail,
            ],
            [
                { content: 'Timesheet Staffing', styles: { fontStyle: 'bold' } },
                data?.StaffingAssistantName,
                `Phone  ${data?.StaffingAssistantFax ?? ''} \nCell ${data?.StaffingAssistantPhone ?? ''}`,
                `Ext ${data?.StaffingAssistantExt ?? ''}`,
                data?.StaffingAssistantForms,
                data?.StaffingAssistantEmail,
            ],
            [
                { content: 'Absences/Substitutes', styles: { fontStyle: 'bold' } },
                data?.SubstitutesName,
                `Phone  ${data?.SubstitutesFax ?? ''} \nCell ${data?.SubstitutesPhone ?? ''}`,
                `Ext ${data?.SubstitutesExt ?? ''}`,
                data?.SubstitutesForms,
                data?.SubstitutesEmail,
            ],
            [
                { content: 'SCOPE HHC', styles: { fontStyle: 'bold' } },
                data?.HealthCareConsultantName,
                `Phone  ${data?.HealthCareConsultantFax ?? ''} \nCell ${data?.HealthCareConsultantPhone ?? ''}`,
                `Ext ${data?.HealthCareConsultantExt ?? ''}`,
                data?.HealthCareConsultantForms,
                data?.HealthCareConsultantEmail,
            ],
            [
                { content: 'Food', styles: { fontStyle: 'bold' } },
                data?.FoodContactName,
                `Phone  ${data?.FoodContactFax ?? ''} \nCell ${data?.FoodContactPhone ?? ''}`,
                `Ext ${data?.FoodContactExt ?? ''}`,
                data?.FoodContactForms,
                data?.FoodContactEmail,
            ],
            [
                { content: 'Supply Contact', styles: { fontStyle: 'bold' } },
                data?.SupplyContactName,
                `Phone  ${data?.SupplyContactFax ?? ''} \nCell ${data?.SupplyContactPhone ?? ''}`,
                `Ext ${data?.SupplyContactExt ?? ''}`,
                data?.SupplyContactForms,
                data?.SupplyContactEmail,
            ],
            [
                { content: 'Special Event Contact', styles: { fontStyle: 'bold' } },
                data?.PettyCashSpecialEventContactName,
                `Phone  ${data?.PettyCashSpecialEventContactFax ?? ''} \nCell ${data?.PettyCashSpecialEventContactPhone ?? ''}`,
                `Ext ${data?.PettyCashSpecialEventContactExt ?? ''}`,
                data?.PettyCashSpecialEventContactForms,
                data?.PettyCashSpecialEventContactEmail,
            ],
            [
                { content: 'SCOPE DSS', styles: { fontStyle: 'bold' } },
                data?.SCOPEDSSName,
                `Phone  ${data?.SCOPEDSSFax ?? ''} \nCell ${data?.SCOPEDSSPhone ?? ''}`,
                `Ext ${data?.SCOPEDSSExt ?? ''}`,
                data?.SCOPEDSSForms,
                data?.SCOPEDSSEmail,
            ],
            [
                { content: 'Presenters/Craft Request', styles: { fontStyle: 'bold' } },
                data?.PresentersName,
                `Phone  ${data?.PresentersFax ?? ''} \nCell ${data?.PresentersPhone ?? ''}`,
                `Ext ${data?.PresentersExt ?? ''}`,
                data?.PresentersForms,
                data?.PresentersEmail,
            ],
            [
                { content: 'Photo IDs', styles: { fontStyle: 'bold' } },
                data?.PhotoIDsName,
                `Phone  ${data?.PhotoIDsFax ?? ''} \nCell ${data?.PhotoIDsPhone ?? ''}`,
                `Ext ${data?.PhotoIDsExt ?? ''}`,
                data?.PhotoIDsForms,
                data?.PhotoIDsEmail,
            ],
        ];

        // Table Styling: Enhanced border and formatting to match the exact design
        autoTable(doc, {
            startY: yOffset,
            head: tableHeaders,
            body: tableBody,
            theme: 'plain',
            styles: {
                fontSize: 10,
                cellPadding: 1.2,      // Trimmed so the larger font still fits one page per site
                lineColor: [0, 0, 0],  // Set line color to black for all borders
                lineWidth: 0.5,        // Increase line width for bolder borders
            },
            headStyles: {
                fontSize: 11,
                fontStyle: 'bold',
                textColor: [0, 0, 0],
                fillColor: [255, 255, 255],  // Ensure white background for header
                halign: 'center',              // Align headers to the left
            },
            bodyStyles: {
                fillColor: [255, 255, 255],  // Ensure white background for body
                textColor: [0, 0, 0],        // Black text for table body
                lineColor: [0, 0, 0],        // Black borders for body cells
            },
            columnStyles: {
                // Widths total 269mm (A4 landscape minus the 14mm side margins). Forms is the
                // column that drives row height, so it gets the slack.
                0: { cellWidth: 42 },  // Adjust cell widths for proper alignment
                1: { cellWidth: 34 },
                // Phone/Cell and Ext are separate columns purely for alignment, so the border
                // between them is switched off and they read as one 'Phones' cell.
                2: { cellWidth: 42, lineWidth: { top: 0.5, bottom: 0.5, left: 0.5, right: 0 } },
                3: { cellWidth: 18, lineWidth: { top: 0.5, bottom: 0.5, left: 0, right: 0.5 } },
                4: { cellWidth: 72 },
                5: { cellWidth: 61 },
            },
            // Bottom margin stops just above the date/page-number footer at height - 10mm
            margin: { top: 20, bottom: 14, left: 14, right: 14 },
            rowPageBreak: 'avoid',  // Never split a single contact across two pages
        });
        yOffset = (doc as any).lastAutoTable.finalY + 10;
    });
        
        // doc.save('Sites-Assignments-report.pdf');
        addPageNumber(doc);
        const pdfBlob = doc.output('blob');
        setPdfBlob(pdfBlob);
    };

    useEffect(() => {
        if (data) {
            generatePDF();
        }
    }, [data]);

    return null;
};

export default memo(SiteAssignmentPdf);
